import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  Bot, Key, Hash, CheckCircle, AlertCircle, Search,
  RefreshCw, Radio, ArrowRight, Users, Loader2,
  Shield, Phone, Lock, ExternalLink, Plus, Trash2, UserCircle
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function SessionPool() {
  const [apiConfig, setApiConfig] = useState(null);
  const [apiId, setApiId] = useState("");
  const [apiHash, setApiHash] = useState("");
  const [phone, setPhone] = useState("");
  const [label, setLabel] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [phoneCodeHash, setPhoneCodeHash] = useState("");
  const [authStep, setAuthStep] = useState("idle");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadApiConfig(); }, []);

  const loadApiConfig = async () => {
    try {
      const { data } = await api.get("/settings/telegram-api");
      setApiConfig(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const saveApiCredentials = async () => {
    if (!apiId.trim() || !apiHash.trim()) { toast.error("API ID ve API Hash gerekli"); return; }
    setSaving(true);
    try {
      await api.post("/settings/telegram-api", { api_id: apiId.trim(), api_hash: apiHash.trim() });
      toast.success("API bilgileri kaydedildi");
      loadApiConfig();
      setAuthStep("phone");
    } catch (err) { toast.error(err.response?.data?.detail || "Kaydetme basarisiz"); }
    finally { setSaving(false); }
  };

  const sendCode = async () => {
    if (!phone.trim()) { toast.error("Telefon numarasi gerekli"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/settings/telegram-api/send-code", { phone: phone.trim(), label: label.trim() || null });
      setPhoneCodeHash(data.phone_code_hash);
      toast.success("Dogrulama kodu gonderildi");
      setAuthStep("code");
    } catch (err) { toast.error(err.response?.data?.detail || "Kod gonderilemedi"); }
    finally { setSaving(false); }
  };

  const verifyCode = async () => {
    if (!code.trim()) { toast.error("Kod gerekli"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/settings/telegram-api/verify-code", { phone: phone.trim(), code: code.trim(), phone_code_hash: phoneCodeHash });
      if (data.needs_2fa) {
        setAuthStep("2fa");
        toast.info("2FA sifresi gerekli");
      } else {
        toast.success("Oturum eklendi!");
        resetForm();
        loadApiConfig();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Dogrulama basarisiz"); }
    finally { setSaving(false); }
  };

  const verify2FA = async () => {
    if (!password.trim()) { toast.error("Sifre gerekli"); return; }
    setSaving(true);
    try {
      await api.post("/settings/telegram-api/verify-2fa", { password: password.trim() });
      toast.success("Oturum eklendi!");
      resetForm();
      loadApiConfig();
    } catch (err) { toast.error(err.response?.data?.detail || "2FA basarisiz"); }
    finally { setSaving(false); }
  };

  const removeSession = async (sessionId) => {
    try {
      await api.post("/settings/telegram-api/remove-session", { session_id: sessionId });
      toast.success("Oturum kaldirildi");
      loadApiConfig();
    } catch (err) { toast.error("Kaldirma basarisiz"); }
  };

  const resetForm = () => {
    setAuthStep("idle");
    setPhone("");
    setLabel("");
    setCode("");
    setPassword("");
    setPhoneCodeHash("");
  };

  if (loading) return null;

  const sessions = apiConfig?.sessions || [];
  const maxSessions = apiConfig?.max_sessions || 6;
  const canAddMore = sessions.length < maxSessions;
  const poolPercent = (sessions.length / maxSessions) * 100;

  return (
    <Card className="bg-[#121212] border-[#27272A]" data-testid="session-pool-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white font-heading flex items-center gap-2 text-base">
            <div className="w-6 h-6 rounded-full bg-[#AF52DE] text-white text-xs flex items-center justify-center font-bold">3</div>
            Telegram API Havuzu
          </CardTitle>
          <Badge variant="outline" className={sessions.length > 0 ? "border-[#34C759] text-[#34C759]" : "border-[#FF9F0A] text-[#FF9F0A]"}>
            {sessions.length}/{maxSessions} Oturum
          </Badge>
        </div>
        <CardDescription className="text-[#A1A1AA] text-sm ml-8">
          Birden fazla Telegram hesabi ekleyerek havuzu buyutun. Daha fazla oturum = daha hizli ve daha fazla kullanici ice aktarma.
        </CardDescription>
      </CardHeader>
      <CardContent className="ml-8 space-y-4">
        {/* Pool Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#A1A1AA]">Havuz Kapasitesi</span>
            <span className="text-white font-medium">{sessions.length}/{maxSessions}</span>
          </div>
          <Progress value={poolPercent} className="h-2 bg-[#27272A]" />
        </div>

        {/* Active Sessions */}
        {sessions.length > 0 && (
          <div className="space-y-2" data-testid="active-sessions">
            {sessions.map((s, i) => (
              <div key={s.id} className="flex items-center justify-between p-3 rounded-lg bg-[#0A0A0A] border border-[#27272A]" data-testid={`session-${s.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#34C759]/10 flex items-center justify-center">
                    <UserCircle className="w-4 h-4 text-[#34C759]" />
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">{s.label || `Oturum ${i + 1}`}</p>
                    <p className="text-xs text-[#A1A1AA] font-mono">{s.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#34C759]/10 text-[#34C759] text-xs">Aktif</Badge>
                  <Button size="sm" variant="ghost" onClick={() => removeSession(s.id)} className="h-7 w-7 p-0 text-[#FF3B30] hover:text-[#FF3B30]" data-testid={`remove-session-${s.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* API Credentials (only if not set) */}
        {!apiConfig?.has_api_credentials && (
          <>
            <div className="p-3 rounded-lg bg-[#AF52DE]/5 border border-[#AF52DE]/20">
              <p className="text-xs text-[#A1A1AA] mb-2">Nasil alinir:</p>
              <ol className="text-xs text-[#A1A1AA] space-y-1 list-decimal list-inside">
                <li><a href="https://my.telegram.org" target="_blank" rel="noopener noreferrer" className="text-[#24A1DE] hover:underline">my.telegram.org</a> adresine gidin</li>
                <li>Telefon numaranizla giris yapin</li>
                <li>"API development tools" bolumune gidin</li>
                <li><span className="text-white">API ID</span> ve <span className="text-white">API Hash</span> degerlerini kopyalayin</li>
              </ol>
            </div>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">API ID</Label>
                <Input data-testid="api-id-input" value={apiId} onChange={(e) => setApiId(e.target.value)} className="bg-[#2C2C2E] border-transparent text-white font-mono" placeholder="12345678" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">API Hash</Label>
                <Input data-testid="api-hash-input" value={apiHash} onChange={(e) => setApiHash(e.target.value)} className="bg-[#2C2C2E] border-transparent text-white font-mono" placeholder="0123456789abcdef..." type="password" />
              </div>
              <Button data-testid="save-api-credentials" onClick={saveApiCredentials} disabled={saving} className="w-full bg-[#AF52DE] hover:brightness-110 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                API Bilgilerini Kaydet
              </Button>
            </div>
          </>
        )}

        {/* Add New Session */}
        {apiConfig?.has_api_credentials && canAddMore && authStep === "idle" && (
          <Button data-testid="add-session-btn" onClick={() => setAuthStep("phone")} variant="outline" className="w-full border-dashed border-[#AF52DE]/30 text-[#AF52DE] hover:bg-[#AF52DE]/10 h-11">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Oturum Ekle ({sessions.length}/{maxSessions})
          </Button>
        )}

        {/* Phone Step */}
        {authStep === "phone" && (
          <div className="space-y-3 p-3 rounded-lg border border-[#AF52DE]/20 bg-[#AF52DE]/5">
            <p className="text-xs text-[#AF52DE] font-medium">Yeni Oturum Ekle</p>
            <div className="space-y-1">
              <Label className="text-[#A1A1AA] text-sm">Etiket (opsiyonel)</Label>
              <Input data-testid="session-label" value={label} onChange={(e) => setLabel(e.target.value)} className="bg-[#2C2C2E] border-transparent text-white" placeholder="Yonetici 1, Admin Ahmet..." />
            </div>
            <div className="space-y-1">
              <Label className="text-[#A1A1AA] text-sm">Telefon Numarasi</Label>
              <Input data-testid="phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-[#2C2C2E] border-transparent text-white font-mono" placeholder="+905xxxxxxxxx" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="send-code-btn" onClick={sendCode} disabled={saving} className="flex-1 bg-[#24A1DE] hover:brightness-110 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
                Kod Gonder
              </Button>
              <Button variant="ghost" onClick={resetForm} className="text-[#A1A1AA]">Iptal</Button>
            </div>
          </div>
        )}

        {/* Code Step */}
        {authStep === "code" && (
          <div className="space-y-3 p-3 rounded-lg border border-[#24A1DE]/20 bg-[#24A1DE]/5">
            <p className="text-xs text-[#24A1DE] font-medium flex items-center gap-1.5">
              <Phone className="w-3 h-3" /> Kod {phone} numarasina gonderildi
            </p>
            <Input data-testid="code-input" value={code} onChange={(e) => setCode(e.target.value)} className="bg-[#2C2C2E] border-transparent text-white font-mono text-center text-lg tracking-widest" placeholder="12345" maxLength={6} />
            <div className="flex gap-2">
              <Button data-testid="verify-code-btn" onClick={verifyCode} disabled={saving} className="flex-1 bg-[#34C759] hover:brightness-110 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                Dogrula
              </Button>
              <Button variant="ghost" onClick={resetForm} className="text-[#A1A1AA]">Iptal</Button>
            </div>
          </div>
        )}

        {/* 2FA Step */}
        {authStep === "2fa" && (
          <div className="space-y-3 p-3 rounded-lg border border-[#FF9F0A]/20 bg-[#FF9F0A]/5">
            <p className="text-xs text-[#FF9F0A] font-medium flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> 2FA sifresi gerekli
            </p>
            <Input data-testid="2fa-input" value={password} onChange={(e) => setPassword(e.target.value)} type="password" className="bg-[#2C2C2E] border-transparent text-white" placeholder="Telegram sifreniz" />
            <div className="flex gap-2">
              <Button data-testid="verify-2fa-btn" onClick={verify2FA} disabled={saving} className="flex-1 bg-[#FF9F0A] hover:brightness-110 text-white">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                2FA Dogrula
              </Button>
              <Button variant="ghost" onClick={resetForm} className="text-[#A1A1AA]">Iptal</Button>
            </div>
          </div>
        )}

        {!canAddMore && sessions.length >= maxSessions && (
          <p className="text-xs text-[#FF9F0A] flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3" /> Maksimum oturum sayisina ulasildi ({maxSessions})
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const [config, setConfig] = useState(null);
  const [botToken, setBotToken] = useState("");
  const [channelInput, setChannelInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [detectedChannels, setDetectedChannels] = useState([]);
  const [verifyResult, setVerifyResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/settings/telegram");
      setConfig(data);
      if (data.main_channel_id) setChannelInput(data.main_channel_id);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const detectChannels = async () => {
    setDetecting(true);
    setDetectedChannels([]);
    try {
      const { data } = await api.post("/settings/detect-channels");
      setDetectedChannels(data.channels);
      if (data.count === 0) toast.info("Kanal bulunamadi");
      else toast.success(`${data.count} kanal bulundu`);
    } catch (err) { toast.error(err.response?.data?.detail || "Algilama basarisiz"); }
    finally { setDetecting(false); }
  };

  const verifyChannel = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.post("/settings/verify-channel");
      setVerifyResult(data);
      if (data.verified) toast.success(`Kanal dogrulandi: ${data.info?.title}`);
    } catch (err) { setVerifyResult({ verified: false, error: err.response?.data?.detail }); }
    finally { setVerifying(false); }
  };

  const saveTokenAndChannel = async () => {
    if (!botToken.trim()) { toast.error("Bot token gerekli"); return; }
    setSaving(true);
    try {
      const payload = { bot_token: botToken };
      if (channelInput.trim()) payload.main_channel_id = channelInput.trim();
      await api.post("/settings/telegram", payload);
      toast.success("Ayarlar kaydedildi");
      setBotToken("");
      loadConfig();
    } catch (err) { toast.error(err.response?.data?.detail || "Kaydetme basarisiz"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="settings-page">
      <h1 className="font-heading text-3xl font-bold text-white mb-6 tracking-tight">Ayarlar</h1>

      <div className="max-w-2xl space-y-6">
        {/* Connection Status */}
        <Card className="bg-[#121212] border-[#27272A]" data-testid="connection-status-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 flex-wrap">
              {config?.is_configured ? (
                <>
                  <CheckCircle className="w-5 h-5 text-[#34C759]" />
                  <span className="text-sm font-medium text-white">Bot Bagli</span>
                  <Badge variant="outline" className="border-[#34C759] text-[#34C759]">Aktif</Badge>
                  {config.bot_token_masked && <span className="text-xs font-mono text-[#A1A1AA]">{config.bot_token_masked}</span>}
                </>
              ) : (
                <>
                  <AlertCircle className="w-5 h-5 text-[#FF9F0A]" />
                  <span className="text-sm font-medium text-white">Bot Bagli Degil</span>
                  <Badge variant="outline" className="border-[#FF9F0A] text-[#FF9F0A]">Yapilandirilmamis</Badge>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Bot Token */}
        <Card className={`bg-[#121212] border-[#27272A] ${config?.is_configured ? "" : "border-[#24A1DE]/30"}`} data-testid="bot-token-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white font-heading flex items-center gap-2 text-base">
                <div className="w-6 h-6 rounded-full bg-[#24A1DE] text-white text-xs flex items-center justify-center font-bold">1</div>
                Bot Token
              </CardTitle>
              {config?.is_configured && <Badge variant="outline" className="border-[#34C759] text-[#34C759] text-xs">Tamamlandi</Badge>}
            </div>
            <CardDescription className="text-[#A1A1AA] text-sm ml-8">@BotFather'dan bot olusturup token'i buraya yapistin</CardDescription>
          </CardHeader>
          <CardContent className="ml-8">
            <Input data-testid="bot-token-input" value={botToken} onChange={(e) => setBotToken(e.target.value)}
              className="bg-[#2C2C2E] border-transparent text-white font-mono text-sm" type="password"
              placeholder={config?.is_configured ? "Yeni token girmek icin yazin..." : "123456:ABCdefGHIjklMNOpqrSTUvwxYZ"} />
            {config?.bot_info?.username && (
              <p className="text-xs text-[#34C759] flex items-center gap-1.5 mt-2"><CheckCircle className="w-3 h-3" />Bagli bot: @{config.bot_info.username}</p>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Channel */}
        <Card className="bg-[#121212] border-[#27272A]" data-testid="channel-config-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white font-heading flex items-center gap-2 text-base">
                <div className="w-6 h-6 rounded-full bg-[#24A1DE] text-white text-xs flex items-center justify-center font-bold">2</div>
                Ana Kanal
              </CardTitle>
              {config?.main_channel_id && <Badge variant="outline" className="border-[#34C759] text-[#34C759] text-xs">Tamamlandi</Badge>}
            </div>
            <CardDescription className="text-[#A1A1AA] text-sm ml-8">Botu kanaliniza yonetici olarak ekleyin, sonra otomatik algilayin</CardDescription>
          </CardHeader>
          <CardContent className="ml-8 space-y-4">
            <Button data-testid="detect-channels-btn" onClick={detectChannels} disabled={detecting || !config?.is_configured}
              variant="outline" className="w-full border-[#24A1DE]/30 text-[#24A1DE] hover:bg-[#24A1DE]/10 h-10">
              {detecting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Ariyorum...</> : <><Search className="w-4 h-4 mr-2" />Kanallari Otomatik Algila</>}
            </Button>
            {detectedChannels.length > 0 && (
              <div className="space-y-2">
                {detectedChannels.map(ch => (
                  <div key={ch.id} className="flex items-center justify-between p-3 rounded-md bg-[#0A0A0A] border border-[#27272A] cursor-pointer" onClick={() => setChannelInput(ch.id)}>
                    <div className="flex items-center gap-3">
                      <Hash className="w-4 h-4 text-[#24A1DE]" />
                      <div>
                        <p className="text-sm text-white font-medium">{ch.title || "Isimsiz"}</p>
                        <p className="text-xs text-[#A1A1AA] font-mono">{ch.username ? `@${ch.username}` : ch.id}</p>
                      </div>
                    </div>
                    <Button size="sm" className="bg-[#24A1DE] hover:brightness-110 text-white h-7 text-xs"
                      onClick={(e) => { e.stopPropagation(); setChannelInput(ch.id); }}>Sec</Button>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-[#A1A1AA] text-sm">Kanal Adi veya ID</Label>
              <Input data-testid="channel-id-input" value={channelInput} onChange={(e) => setChannelInput(e.target.value)}
                className="bg-[#2C2C2E] border-transparent text-white font-mono text-sm" placeholder="@kanaladiniz veya -1001234567890" />
            </div>
          </CardContent>
        </Card>

        {/* Save */}
        <Button data-testid="save-settings-btn" onClick={saveTokenAndChannel} disabled={saving || (!botToken && !channelInput)}
          className="w-full bg-[#24A1DE] hover:brightness-110 text-white h-11 text-base">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Kaydediliyor...</> : "Kaydet"}
        </Button>

        {/* Step 3: Session Pool */}
        <SessionPool />

        {/* Help */}
        <Card className="bg-[#121212] border-[#27272A]">
          <CardHeader className="pb-3">
            <CardTitle className="text-white font-heading text-base">Adim Adim Rehber</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-[#A1A1AA]">
              <div className="flex gap-3"><span className="text-[#24A1DE] font-bold shrink-0 w-5 text-center">1</span><p>@BotFather'da /newbot ile bot olusturun</p></div>
              <Separator className="bg-[#27272A]" />
              <div className="flex gap-3"><span className="text-[#24A1DE] font-bold shrink-0 w-5 text-center">2</span><p>Botu kanaliniza <span className="text-white">yonetici</span> olarak ekleyin</p></div>
              <Separator className="bg-[#27272A]" />
              <div className="flex gap-3"><span className="text-[#24A1DE] font-bold shrink-0 w-5 text-center">3</span><p>Kanali otomatik algilatin</p></div>
              <Separator className="bg-[#27272A]" />
              <div className="flex gap-3"><span className="text-[#AF52DE] font-bold shrink-0 w-5 text-center">4</span><p><span className="text-[#AF52DE]">Telegram API havuzu</span>na 1-6 hesap ekleyin</p></div>
              <Separator className="bg-[#27272A]" />
              <div className="flex gap-3"><span className="text-[#34C759] font-bold shrink-0 w-5 text-center">5</span><p>Kullanicilar sayfasindan <span className="text-[#34C759]">tum aboneleri ice aktarin</span></p></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
